import { useTranslation } from 'react-i18next';
import { AI_CATEGORIES, type AdminOverview, type AiCategory } from '@b4b/shared';
import { useTopicLabel } from '../../lib/topics.js';
import { formatPercent } from './format.js';

const KNOWN_CATEGORIES: readonly string[] = AI_CATEGORIES;

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <dt className="text-xs text-neutral-500">{label}</dt>
      <dd className="text-lg font-semibold">{value}</dd>
    </div>
  );
}

export default function QualityPanel({ overview }: { overview: AdminOverview }) {
  const { t, i18n } = useTranslation();
  const topicLabel = useTopicLabel();
  // AI-assigned categories should always be one of AI_CATEGORIES, but fall back to the raw
  // slug for anything unexpected rather than showing a raw i18next key.
  const categoryLabel = (category: string): string => (KNOWN_CATEGORIES.includes(category) ? topicLabel(category as AiCategory) : category);
  const { dedup, tags, images, breaking } = overview;
  const maxBucket = Math.max(1, ...dedup.sizeHistogram.map((b) => b.count));
  return (
    <section className="space-y-4 rounded-2xl border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-900">
      <h2 className="font-semibold">{t('admin.quality.title')}</h2>
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label={t('admin.quality.articles')} value={dedup.articles} />
        <Stat label={t('admin.quality.clusters')} value={dedup.clusters} />
        <Stat label={t('admin.quality.multiSource')} value={formatPercent(dedup.multiSourceShare, i18n.language)} />
        <Stat label={t('admin.quality.embeddingCoverage')} value={formatPercent(dedup.embeddingCoverage, i18n.language)} />
        <Stat label={t('admin.quality.tagCoverage')} value={formatPercent(tags.coverage, i18n.language)} />
        <Stat label={t('admin.quality.emptyShare')} value={formatPercent(tags.emptyShare, i18n.language)} />
        <Stat label={t('admin.quality.images')} value={`${images.generated} ${t('admin.quality.imagesGenerated')} · ${images.failed} ${t('admin.quality.imagesFailed')}`} />
        <Stat label={t('admin.quality.breaking')} value={`${breaking.notifications} · ${t('admin.quality.breakingClusters', { count: breaking.clusters })}`} />
      </dl>

      <div>
        <h3 className="mb-1 text-xs text-neutral-500">{t('admin.quality.sizeHistogram')}</h3>
        <ul className="space-y-1">
          {dedup.sizeHistogram.map((b) => (
            <li key={b.bucket} className="flex items-center gap-2 text-sm">
              <span className="w-8 text-right tabular-nums">{b.bucket}</span>
              <span className="h-3 rounded bg-accent/70" style={{ width: `${(b.count / maxBucket) * 100}%`, minWidth: b.count ? 4 : 0 }} aria-hidden />
              <span className="tabular-nums text-neutral-500">{b.count}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <h3 className="mb-1 text-xs text-neutral-500">{t('admin.quality.categories')}</h3>
          <ul className="text-sm">
            {tags.categories.map((c) => (
              <li key={c.category} className="flex justify-between"><span>{categoryLabel(c.category)}</span><span className="tabular-nums text-neutral-500">{c.count}</span></li>
            ))}
          </ul>
        </div>
        <div>
          <h3 className="mb-1 text-xs text-neutral-500">{t('admin.quality.topTags')}</h3>
          <div className="flex flex-wrap gap-1.5">
            {tags.topTags.map((x) => (
              <span key={x.tag} className="rounded-full bg-neutral-100 px-2.5 py-1 text-xs dark:bg-neutral-800">#{x.tag} <span className="text-neutral-500">{x.uses}</span></span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
